import { createContext, useContext, ReactNode, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useLocalStorageState from 'src/common/hooks/useLocalStorageState';
import { DecodedJWTUser, LocalStorageKeys } from 'src/types/common';
import { useDecodeJWT } from '../hooks/useDecodeJWT';
import RouteAccessFallback from 'src/routes/RouteAccessFallback';

interface VerificationTokenContextType {
  verificationToken: string | null;
  decodedUser: DecodedJWTUser | null;
}

const VerificationTokenContext = createContext<
  VerificationTokenContextType | undefined
>(undefined);

export const useVerificationToken = () => {
  const context = useContext(VerificationTokenContext);
  if (!context) {
    throw new Error(
      'useVerificationToken must be used within a VerificationTokenProvider'
    );
  }
  return context;
};

interface VerificationTokenProviderProps {
  children: ReactNode;
}

export const VerificationTokenProvider = ({
  children,
}: VerificationTokenProviderProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [verificationToken, setVerificationToken] = useLocalStorageState(
    LocalStorageKeys.VerificationToken,
    null
  );
  const [tokenExp, setTokenExp] = useLocalStorageState(
    LocalStorageKeys.CartExpirationTime,
    null
  );

  const queryToken = searchParams.get('token');
  const token = queryToken || verificationToken;

  const { decodedUser } = useDecodeJWT(token);

  useEffect(() => {
    if (!token) {
      return;
    }

    if (verificationToken && !queryToken) {
      setSearchParams({ token: verificationToken });
    }

    if (decodedUser && verificationToken !== token) {
      setVerificationToken(token);
    }

    if (decodedUser?.exp && tokenExp !== decodedUser.exp) {
      setTokenExp(decodedUser.exp);
    }
  }, [
    token,
    verificationToken,
    queryToken,
    decodedUser,
    navigate,
    setSearchParams,
    setVerificationToken,
  ]);

  return (
    <VerificationTokenContext.Provider
      value={{
        verificationToken: token,
        decodedUser,
      }}
    >
      {token ? children : <RouteAccessFallback variant="unauthorized" />}
    </VerificationTokenContext.Provider>
  );
};
